<script setup>
import { VueFinalModal } from 'vue-final-modal';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import { useTerraStore } from '~/terra/store/terra.store';
import { useThermStore } from '~/therm/store/therm.store';

const form$ = ref(null);
const form = ref({});
const { $t, $toast, $date, $services, route, auth_store } = useCommonImports();

const state = reactive({
  sync_status: null,
  open_popup: false,
  is_loading: false,
  is_syncing: false,
});

const show_sync_trigger = computed(() => {
  if (route.name === 'maps-list')
    return false;
  if (['core', 'tasks', 'dms', 'inventory'].includes(getService()))
    return true;
  else if (getService() === 'terra' && ['terra-viewer', 'maps'].includes(route.name))
    return true;
  else if (getService() === 'therm' && ['therm'].includes(route.name))
    return true;
  else if (getService() === 'forms' && route.name.includes('fam'))
    return true;
  else if (getService() === 'pm' && ['schedule-details'].includes(route.name))
    return true;
  return false;
});

function getSyncPayload() {
  if (getService() === 'terra') {
    const terra_store = useTerraStore();
    return {
      container_name: terra_store.container.name,
      container_id: route.params.id,
    };
  }
  else if (getService() === 'therm') {
    const therm_store = useThermStore();
    return {
      container_name: therm_store.container.name,
      container_uid: route.params.id,
    };
  }
  else if (getService() === 'forms') {
    return { template: route.params.template_uid };
  }
  else if (getService() === 'pm') {
    return { schedule_uid: route.params.schedule_uid };
  }
  return {};
}

async function save() {
  try {
    if (form.value.confirm !== 'confirm')
      return;
    await $services.data_warehouse.sync({
      body: {
        sync: true,
        sync_schema_only: form.value.what_to_sync,
        service: getService(),
        sync_payload: getSyncPayload(),
        asset_uid: route.params.asset_id,
        organization_uid: auth_store.current_organization_uid,
      },
    });
    state.open_popup = !state.open_popup;
    $toast({
      title: $t('Success'),
      text: 'Sync initiated successfully',
      type: 'success',
    });
  }
  catch {
    state.is_syncing = false;
    $toast({
      title: $t('Something went wrong'),
      text: $t('Please try again'),
      type: 'error',
    });
  }
}

function getService() {
  if (['files', 'transmittals'].includes(route.meta.requireModuleAccess))
    return 'dms';
  if (['tasks', 'forms', 'terra', 'therm', 'pm', 'inventory'].includes(route.meta.requireModuleAccess))
    return route.meta.requireModuleAccess;
  return 'core';
}

async function openModal() {
  state.is_loading = true;
  const { data } = await $services.data_warehouse.getSyncStatus({
    query: {
      service: getService(),
    },
  });
  state.sync_status = data.status;
  state.open_popup = !state.open_popup;
  state.is_loading = false;
}
</script>

<template>
  <!--
    This button is shown on the Navbar for users who have permission. This allows manually syncing data from a certain module into BI.
    Schema only -> Changes/Adapts to column name changes only (Rarely used)
    Everything -> Includes data changes (Mostly used)
  -->
  <div>
    <HawkButton
      v-if="show_sync_trigger"
      v-tippy="{ content: `Sync ${getService()} data`, placement: 'bottom' }"
      icon
      type="text"
      size="md"
      class="flex hover:bg-gray-900"
      @click="openModal"
    >
      <IconHawkLoadingCircle v-if="state.is_loading" class="animate-spin" />
      <IconHawkDatabaseOne v-else class="!text-white" />
    </HawkButton>
    <VueFinalModal
      v-model="state.open_popup" class="flex justify-center items-center"
      content-class="bg-white border space-t-2 rounded-xl w-[600px] max-w-4xl border-0" overlay-transition="vfm-fade"
      content-transition="vfm-fade"
    >
      <hawk-modal-header @close="state.open_popup = !state.open_popup">
        <template #title>
          Sync data in warehouse
        </template>
      </hawk-modal-header>
      <Vueform
        ref="form$" v-model="form" size="sm" :display-errors="false" :columns="{
          default: { container: 12, label: 5, wrapper: 12 },
          sm: { container: 12, label: 5, wrapper: 12 },
        }" :endpoint="save"
      >
        <div class="col-span-12">
          <hawk-modal-content :is_scroll="false">
            <div class="text-sm font-medium text-gray-700">
              Sync or reset whole data or adjust the schema for '{{ getService() }}' as needed. Below are the latest
              timestamps for the respective operations.
            </div>
            <ul class="text-sm my-2 text-gray-700">
              <li>
                <span class="font-semibold">Processed:</span> {{ state.sync_status.processed
                  ? $date(state.sync_status.processed) : 'N/A' }}
              </li>
              <li>
                <span class="font-semibold">Successful:</span> {{ state.sync_status.successful
                  ? $date(state.sync_status.successful) : 'N/A' }}
              </li>
              <li>
                <span class="font-semibold">Failed:</span> {{ state.sync_status.failed
                  ? $date(state.sync_status.failed)
                  : 'N/A' }}
              </li>
            </ul>
            <RadiogroupElement
              name="what_to_sync" label="What do you want to sync?" :items="[
                { value: true, label: 'Schema only' },
                { value: false, label: 'Everything' },
              ]" :default="true" class="mb-6"
            />
            <TextElement name="confirm" label="Type 'confirm' to proceed and sync" rules="required" />
          </hawk-modal-content>
          <hawk-modal-footer>
            <template #right>
              <div class="flex justify-end gap-4">
                <HawkButton type="outlined" @click="state.open_popup = !state.open_popup">
                  {{ $t('Cancel') }}
                </HawkButton>
                <ButtonElement
                  :disabled="form.confirm !== 'confirm'" button-class="w-full bg-blue-600" name="submit"
                  :submits="true"
                >
                  {{ $t('Sync') }}
                </ButtonElement>
              </div>
            </template>
          </hawk-modal-footer>
        </div>
      </Vueform>
    </VueFinalModal>
  </div>
</template>
